import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import {
	AppBar,
	Box,
	Divider,
	Grid,
	Paper,
	Stack,
	Toolbar,
	Typography
} from '@mui/material';
import Link from '@mui/material/Link';
import { alpha, styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import AuthService from '../../services/authService';
import ActiveTickets from '../activeTickets/ActiveTickets';
import '../head/head.scss';
import AuthenticatedContent from '../../common/AuthenticatedContent';

const BTN_HIDE_DELAY = 5000;

function useBtnVisibility() {
	const [visible, setVisible] = useState(true);
	const timer = useRef(null);
	const schedule = useCallback(() => {
		clearTimeout(timer.current);
		setVisible(true);
		timer.current = setTimeout(() => setVisible(false), BTN_HIDE_DELAY);
	}, []);
	useEffect(() => {
		schedule();
		window.addEventListener('mousemove', schedule);
		return () => {
			window.removeEventListener('mousemove', schedule);
			clearTimeout(timer.current);
		};
	}, [schedule]);
	return visible;
}

/** === helper: фирменный SVG-орнамент в стиле волн и окружностей === */
function makeCorporatePattern(theme) {
	const base =
		theme.palette.mode === 'dark'
			? alpha(theme.palette.primary.light, 0.2)
			: alpha(theme.palette.primary.main, 0.12);
	const stroke =
		theme.palette.mode === 'dark'
			? alpha(theme.palette.secondary.light, 0.3)
			: alpha(theme.palette.secondary.main, 0.25);

	const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
  <defs>
    <pattern id="waves" width="60" height="60" patternUnits="userSpaceOnUse">
      <path d="M0 30 Q15 10 30 30 T60 30" stroke="${stroke}" stroke-width="1" fill="none"/>
      <path d="M0 45 Q15 25 30 45 T60 45" stroke="${base}" stroke-width="1" fill="none"/>
      <circle cx="15" cy="15" r="2" fill="${stroke}" />
      <circle cx="45" cy="45" r="2" fill="${stroke}" />
    </pattern>
  </defs>
  <rect width="120" height="120" fill="url(#waves)"/>
</svg>`.trim();

	return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

export const Head2 = () => {
	console.info('[FIX] Head2: Mounting dff6b34f design (AppBar + Hero + YouTube iframe)');
	const btnVisible = useBtnVisibility();

	return (
		<>
			{/* HEADER */}
			<AppBar
				position='sticky'
				elevation={0}
				sx={theme => ({
					background: `linear-gradient(90deg,
            ${alpha(theme.palette.primary.main, 0.95)} 0%,
            ${alpha(theme.palette.primary.main, 0.9)} 60%,
            ${alpha(theme.palette.primary.main, 0.85)} 100%)`,
					boxShadow: 'none',
					backdropFilter: 'saturate(120%)',
					color: '#fff'
				})}
			>
				<Toolbar
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						gap: 2,
						px: { xs: 2, md: 3 },
						py: 1.25,
						'@keyframes livePulse': {
							'0%': { opacity: 0.6, transform: 'scale(0.95)' },
							'50%': { opacity: 1, transform: 'scale(1)' },
							'100%': { opacity: 0.6, transform: 'scale(0.95)' }
						}
					}}
				>
					<Stack direction='row' alignItems='center' spacing={1.5}>
						<BrandLogo src='/logo5.png' alt='Dulaty University' />
						<Box>
							<Typography
								variant='subtitle1'
								sx={{ fontWeight: 800, lineHeight: 1 }}
							>
								DU-Queue
							</Typography>
							<Typography variant='caption' sx={{ opacity: 0.85 }}>
								Dulaty University Queue System
							</Typography>
						</Box>
					</Stack>

					<Stack direction='row' spacing={2} alignItems='center'>
						{/* Live-индикатор */}
						<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
							<Box
								sx={{
									width: 8,
									height: 8,
									borderRadius: '50%',
									bgcolor: '#fff',
									animation: 'livePulse 1.8s ease-in-out infinite',
									boxShadow: '0 0 0 2px rgba(255,255,255,0.35)'
								}}
							/>
							<Typography
								variant='body2'
								sx={{ fontWeight: 800, letterSpacing: 0.5 }}
							>
								Live
							</Typography>
						</Box>
						{!AuthService.isLoggedIn() && (
							<Link
								component={RouterLink}
								to='/login'
								underline='hover'
								color='inherit'
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: 1,
									fontWeight: 800,
									px: 0.75,
									py: 0.5,
									borderRadius: 1,
									transition: 'background-color .2s ease',
									'&:hover': { backgroundColor: 'rgba(255,255,255,.08)' }
								}}
							>
								<LoginRoundedIcon fontSize='small' />
								Вход
							</Link>
						)}
						{AuthService.isLoggedIn() && (
							<>
								<AuthenticatedContent roles={['MANAGER']}>
									<Link
										component={RouterLink}
										to='/manager'
										underline='hover'
										color='inherit'
										sx={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: 1,
											fontWeight: 800,
											px: 0.75,
											py: 0.5,
											borderRadius: 1,
											transition: 'background-color .2s ease',
											'&:hover': { backgroundColor: 'rgba(255,255,255,.08)' }
										}}
									>
										<LoginRoundedIcon fontSize='small' />
										Кабинет менеджера
									</Link>
								</AuthenticatedContent>
								<Link
									component={RouterLink}
									onClick={AuthService.logout}
									underline='hover'
									color='inherit'
									sx={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: 1,
										fontWeight: 800,
										px: 0.75,
										py: 0.5,
										borderRadius: 1,
										transition: 'background-color .2s ease',
										'&:hover': { backgroundColor: 'rgba(255,255,255,.08)' }
									}}
								>
									Выход
								</Link>
							</>
						)}
					</Stack>
				</Toolbar>
			</AppBar>

			{/* Hero-блок */}
			<Hero
				sx={{
					py: { xs: 3, md: 6 },
					px: { xs: 2, md: 4 }
				}}
			>
				<Grid
					container
					spacing={3}
					alignItems='stretch'
					justifyContent='space-between'
				>
					{/* Левая колонка */}
					<Grid item xs={12} md={6}>
						<LeftCard elevation={0} variant='outlined'>
							<Stack spacing={2.5}>
								<Typography
									variant='h3'
									sx={{ fontWeight: 800, lineHeight: 1.1 }}
								>
									Система электронной очереди
								</Typography>

								<Divider sx={{ my: 0.5 }} />

								{/* Видео-зона */}
								<Banner>
									<iframe
										width='100%'
										height='100%'
										src='https://www.youtube.com/embed/Ct4AsRLngG4?autoplay=1&mute=1&rel=0&loop=1'
										title='YouTube video player'
										frameBorder='0'
										allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
										referrerPolicy='strict-origin-when-cross-origin'
										allowFullScreen
										style={{ borderRadius: 14, border: 0 }}
									/>
								</Banner>
							</Stack>
						</LeftCard>
					</Grid>

					{/* Правая колонка */}
					<Grid item xs={12} md={6}>
						<RightCard elevation={0} variant='outlined'>
							<ActiveTickets />
						</RightCard>
					</Grid>
				</Grid>
			</Hero>

			{/* Кнопки переключения дизайна */}
			<SwitchBtnGroup sx={{ opacity: btnVisible ? 1 : 0, pointerEvents: btnVisible ? 'auto' : 'none' }}>
				<DesignSwitchBtn component={RouterLink} to='/home3'>
					Дизайн 1
				</DesignSwitchBtn>
				<DesignSwitchBtn component={RouterLink} to='/'>
					Дизайн 3
				</DesignSwitchBtn>
			</SwitchBtnGroup>
		</>
	);
};

/* ====== СТИЛИ (из коммита dff6b34f) ====== */

const Hero = styled(Box)(({ theme }) => {
	const pattern = makeCorporatePattern(theme);
	return {
		minHeight: 'calc(100vh - 24px)',
		display: 'flex',
		paddingTop: '10px',
		flexDirection: 'column',
		alignItems: 'center',
		backgroundImage: `
      linear-gradient(
        180deg,
        ${alpha(
					theme.palette.primary.main,
					theme.palette.mode === 'dark' ? 0.06 : 0.1
				)} 0%,
        ${alpha(theme.palette.background.paper, 0.96)} 100%
      ),
      ${pattern}
    `,
		backgroundRepeat: 'no-repeat, repeat',
		backgroundSize: 'cover, 200px 200px',
		backgroundAttachment: 'scroll, fixed'
	};
});

const CardBase = styled(Paper)(({ theme }) => ({
	height: '100%',
	borderRadius: 18,
	padding: theme.spacing(3),
	borderColor: theme.palette.divider,
	backgroundColor:
		theme.palette.mode === 'dark'
			? alpha(theme.palette.background.paper, 0.92)
			: theme.palette.background.paper,
	boxShadow: theme.shadows[2],
	position: 'relative',
	overflow: 'hidden'
}));

const LeftCard = styled(CardBase)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	gap: theme.spacing(2),
	'&::before': {
		content: '""',
		position: 'absolute',
		left: 0,
		top: 0,
		bottom: 0,
		width: 6,
		background:
			theme.palette.mode === 'dark'
				? alpha(theme.palette.primary.light, 0.7)
				: alpha(theme.palette.primary.main, 0.9)
	}
}));

const RightCard = styled(CardBase)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	backgroundImage: `radial-gradient(circle at 110% -10%, ${alpha(
		theme.palette.primary.main,
		0.08
	)} 0%, rgba(0,0,0,0) 55%)`,
	backgroundRepeat: 'no-repeat'
}));

const Banner = styled('div')(() => ({
	width: '100%',
	height: 260,
	borderRadius: 14,
	overflow: 'hidden',
	display: 'flex',
	alignItems: 'stretch',
	justifyContent: 'center'
}));

const BrandLogo = styled('img')(() => ({
	width: 150,
	height: 40,
	objectFit: 'contain',
	borderRadius: 8,
	filter: 'brightness(0) invert(1)'
}));

const SwitchBtnGroup = styled(Box)(() => ({
	position:   'fixed',
	bottom:     '1.25rem',
	right:      '1.25rem',
	zIndex:     1100,
	display:    'flex',
	gap:        '0.5rem',
	transition: 'opacity 0.6s ease',
}));

const DesignSwitchBtn = styled(Link)(() => ({
	padding:        '0.4rem 0.9rem',
	borderRadius:   '0.6rem',
	fontWeight:     700,
	fontSize:       '0.75rem',
	color:          'rgba(0,0,0,0.6)',
	background:     'rgba(0,0,0,0.08)',
	backdropFilter: 'blur(8px)',
	border:         '1px solid rgba(0,0,0,0.15)',
	textDecoration: 'none',
	cursor:         'pointer',
	transition:     'background 0.2s ease, color 0.2s ease',
	'&:hover': {
		background: 'rgba(0,0,0,0.15)',
		color:      'rgba(0,0,0,0.9)',
	},
}));

export default Head2;
